import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import uniqueId from 'lodash/uniqueId'
import { cx } from '@emotion/css'
import LoaderIcon from '@offerzen/zenicon/LoaderIcon'
import CloseIcon from '@offerzen/zenicon/CloseIcon'
import SearchIcon from '@offerzen/zenicon/SearchIcon'
import colors from '../../themes/colors'
import FormErrorMessage from '../FormErrorMessage'
import FormHelpText from '../FormHelpText'
import AccessibleIcon from '../AccessibleIcon'
import styles, { fieldTheme, itemTheme } from './styles'

const SingleSearch = ({
  className,
  helpText,
  onChange,
  onFocus,
  onSearch,
  onClear,
  onBlur,
  onKeyDown,
  meta = {},
  input = {},
  value = '',
  results = [],
  fetching = false,
  theme = 'primary',
  ...props
}) => {
  const [open, setOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [options, setOptions] = useState([])

  const wrapperRef = useRef(null)
  const bodyRef = useRef(null)
  const inputRef = useRef(null)
  const prevFetching = useRef(false)
  const componentId = useRef(uniqueId('single-search-items-'))

  useEffect(() => {
    if (!prevFetching.current && fetching) {
      setOptions([])
      setSelectedIndex(-1)
    }
    if (prevFetching.current && !fetching) {
      setOpen(true)
      setOptions(results)
    }
    if (prevFetching.current !== fetching) {
      prevFetching.current = fetching
    }
  }, [fetching, results])

  useEffect(() => {
    const handleClosingClick = e => {
      if (wrapperRef.current.contains(e.target)) return
      setOpen(false)
    }

    if (open) {
      document.addEventListener('mousedown', handleClosingClick)
    } else {
      document.removeEventListener('mousedown', handleClosingClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClosingClick)
    }
  }, [open])

  const inputValueObject = typeof input.value === 'string' ? { label: input.value } : input.value
  const valueObject = inputValueObject || { label: value }
  const handler = input.onChange || onChange
  const errored = meta.touched && meta.error && selectedIndex === -1

  const displayIcon = ({ isLoading, inputHasText }) => {
    if (isLoading) return LoaderIcon
    if (inputHasText) {
      return CloseIcon
    }
    return SearchIcon
  }

  const clearInput = () => {
    const clearHandler = input.onChange || onChange
    if (clearHandler) {
      clearHandler({ label: '' })
    }
    if (onClear) {
      onClear()
    }
    setOpen(false)
    setSelectedIndex(-1)
    setOptions([])
  }

  const triggerFetch = debounce(query => {
    if (query === '') {
      clearInput()
      return
    }

    onSearch(query)
  }, 150)

  const handleNavigation = ev => {
    if (ev.keyCode === 38 || ev.keyCode === 40) {
      // If arrows up and down were pressed
      ev.preventDefault()

      // Pressing down in options +1, pressing up in options -1
      const localSelectedIndex = selectedIndex + (ev.keyCode - 39)
      if (localSelectedIndex >= 0 && localSelectedIndex < options.length) {
        setSelectedIndex(localSelectedIndex)

        // If the body is scrollable, make sure the selected option is visible
        // Also, we're making sure that the reference exists to avoid null errors
        if (bodyRef && bodyRef.current && bodyRef.current.scrollHeight > bodyRef.current.offsetHeight) {
          // Get the reference of the new selected option
          const selectedOption = bodyRef.current.children[localSelectedIndex]
          // Calculate the visible area of the menu
          const visibleArea = bodyRef.current.scrollTop + bodyRef.current.offsetHeight

          if (selectedOption.offsetTop < bodyRef.current.scrollTop) {
            selectedOption.scrollIntoView()
          } else if (selectedOption.offsetTop + selectedOption.offsetHeight > visibleArea) {
            bodyRef.current.scrollTo({ top: bodyRef.current.scrollTop + selectedOption.offsetHeight })
          }
        }
      }
    } else if (ev.keyCode === 27) {
      // If esc was pressed
      ev.preventDefault()
      setOpen(false)
      setSelectedIndex(-1)
      setOptions([])
    } else if (ev.keyCode === 13) {
      // If enter was pressed
      ev.preventDefault()
      const changeHandler = input.onChange || onChange
      const item = options[selectedIndex]
      const freeTextItem = ev.target.value

      if (changeHandler) {
        if (item) changeHandler({ value: item.value, label: item.label })
        else changeHandler({ value: null, label: freeTextItem })
      }
      setOpen(false)
      setSelectedIndex(-1)
      setOptions([])
      if (inputRef) {
        inputRef.current.blur()
      }
    }

    if (onKeyDown) {
      onKeyDown(ev)
    }
  }

  const handleInputChange = ev => {
    triggerFetch(ev.target.value)
    const changeHandler = input.onChange || onChange
    if (changeHandler) changeHandler({ label: ev.target.value })
  }

  const handleInputBlur = () => {
    const blurHandler = input.onBlur || onBlur
    if (blurHandler) {
      blurHandler()
    }
    if (selectedIndex === -1 && open) {
      setOpen(false)
      setSelectedIndex(-1)
      setOptions([])
    }
  }

  const handleInputFocus = ev => {
    const focusHandler = input.onFocus || onFocus
    if (focusHandler) {
      focusHandler(ev)
    }
    if (!valueObject || !valueObject.label) {
      return
    }
    setSelectedIndex(-1)
    setOptions([])
    triggerFetch(valueObject.label)
  }

  const handleItemClick = item => {
    if (handler) handler({ value: item.value, label: item.label })
    setOpen(false)
    setSelectedIndex(-1)
    setOptions([])
  }

  const handleItemFocus = idx => {
    setSelectedIndex(idx)
  }

  const handleItemBlur = () => {
    setSelectedIndex(-1)
  }

  return (
    <div className={cx(styles.wrapper, className)} ref={wrapperRef}>
      {/* Render body first so the "input" appears on top */}
      {Boolean(open && options.length > 0) && (
        <ul className={styles.body} ref={bodyRef} id={componentId.current} role="listbox">
          {options.map((item, idx) => (
            <li
              key={item.value}
              role="option"
              aria-selected={idx === selectedIndex}
              className={cx(styles.item, itemTheme(theme, idx === selectedIndex))}
              onMouseOver={() => handleItemFocus(idx)}
              onFocus={() => handleItemFocus(idx)}
              onMouseLeave={() => handleItemFocus(-1)}
              onMouseOut={handleItemBlur}
              onBlur={handleItemBlur}
              onClick={() => handleItemClick(item)}
              onKeyUp={() => handleItemClick(item)}
              id={`${componentId.current}-${idx}`}>
              {item.label}
            </li>
          ))}
        </ul>
      )}
      <div className={cx(styles.field, fieldTheme(theme), open && styles.openField, errored && styles.fieldError)}>
        <input
          className={styles.input}
          type="text"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...{ ...input, ...props }}
          value={valueObject.label}
          autoComplete="off"
          ref={inputRef}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleNavigation}
          role="combobox"
          aria-expanded={open}
          aria-controls={componentId.current}
          aria-activedescendant={selectedIndex === -1 ? '' : `${componentId.current}-${selectedIndex}`}
        />

        <button
          type="button"
          className={cx(styles.icon, fetching && styles.iconSpinning, valueObject.label && styles.iconClear)}
          onClick={clearInput}
          aria-label="Clear input">
          <AccessibleIcon
            icon={displayIcon({ isLoading: fetching, inputHasText: valueObject.label })}
            size={20}
            color={errored ? colors.mandy : colors.geyser}
          />
        </button>
      </div>
      {Boolean(helpText && !errored) && <FormHelpText text={helpText} />}
      {Boolean(errored) && <FormErrorMessage text={meta.error} />}
    </div>
  )
}

const valuePropTypes = PropTypes.oneOfType([
  PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  PropTypes.string,
])
const inputEventPropTypes = {
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
}

SingleSearch.propTypes = {
  className: PropTypes.string,
  fetching: PropTypes.bool,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'dark', 'tertiary', 'info']),
  input: PropTypes.shape({
    value: valuePropTypes,
    ...inputEventPropTypes,
  }),
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  value: valuePropTypes,
  ...inputEventPropTypes,
  helpText: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
  onClear: PropTypes.func,
}

export default SingleSearch
